import { OptionTooltip } from "@/workflow/components/configurations/form/components/OptionTooltip";
import { Badge, Button, Icon, TextField } from "@elaniintc/goes-design-system";
import { DataCenterField } from "@/pages/digital-files/entity/components/DataCenterField";
import { ALLOWED_FILE_TYPES } from "@/pages/digital-files/utils";
import type { RenderFieldContext } from "./types";
import { removeAtIndex } from "./utils";

export function renderLocalListField(ctx: RenderFieldContext) {
  const {
    fieldConfig,
    formValues,
    updateFormValue,
    localListItems,
    setLocalListItems,
    newListItem,
    setNewListItem,
  } = ctx;
  const isDropdownList = fieldConfig.key === "localList";
  const currentListItems = (() => {
    if (isDropdownList) return localListItems;
    if (Array.isArray(formValues[fieldConfig.key]))
      return formValues[fieldConfig.key] as string[];
    return [];
  })();

  const handleAddItem = () => {
    if (!newListItem.trim()) return;
    if (isDropdownList) {
      setLocalListItems([...currentListItems, newListItem.trim()]);
    } else {
      updateFormValue(fieldConfig.key, [
        ...currentListItems,
        newListItem.trim(),
      ]);
    }
    setNewListItem("");
  };

  const handleRemoveItem = (index: number) => {
    const updatedItems = removeAtIndex(currentListItems, index);
    if (isDropdownList) {
      setLocalListItems(updatedItems);
    } else {
      updateFormValue(fieldConfig.key, updatedItems);
    }
  };

  return (
    <div key={fieldConfig.key} className="flex flex-col gap-1">
      <label
        htmlFor={fieldConfig.key}
        className="font-semibold text-body-loud leading-2xl"
      >
        {fieldConfig.label}
        {fieldConfig.required && <span className="text-danger">*</span>}
      </label>
      <div className="flex items-end pt-4">
        <div className="w-full">
          <TextField
            label=""
            placeholder="Añade un nuevo item"
            className="rounded-tr-none rounded-br-none"
            value={newListItem}
            onChange={(e) => setNewListItem(e.target.value)}
          />
        </div>
        <Button
          className="mb-1 h-11 rounded-bl-none rounded-tl-none border-l-0"
          variant="quiet"
          onClick={handleAddItem}
        >
          <Icon name="plus" type="far" />
        </Button>
      </div>
      {fieldConfig.helperText && (
        <p className="text-xs mt-1 text-info">
          <Icon
            name="circle-info"
            type="far"
            size="sm"
            className="inline-block mr-2"
          />
          {fieldConfig.helperText}
        </p>
      )}
      {currentListItems.length > 0 && (
        <div className="w-full max-w-2xl rounded-xl border border-neutral bg-neutral-object shadow-sm backdrop-blur p-1 mt-2">
          <ul className="px-2">
            {currentListItems.map((item: string, index: number) => (
              <li
                key={`${item}-${index}`}
                className="flex items-center justify-between py-1"
              >
                <span className="text-base text-body-quiet">{item}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  type="button"
                  aria-label="Eliminar"
                  className="shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-full text-body-quiet"
                  onClick={() => handleRemoveItem(index)}
                >
                  <Icon name="xmark" type="far" />
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function renderFileTypesField(ctx: RenderFieldContext) {
  const {
    fieldConfig,
    formValues,
    updateFormValue,
    newFileType,
    setNewFileType,
  } = ctx;
  const currentFileTypes = Array.isArray(formValues[fieldConfig.key])
    ? (formValues[fieldConfig.key] as string[])
    : [];
  return (
    <div key={fieldConfig.key} className="flex flex-col gap-1">
      <label
        htmlFor={fieldConfig.key}
        className="font-semibold text-body-loud leading-2xl"
      >
        {fieldConfig.label}
        {fieldConfig.required && <span className="text-danger">*</span>}
      </label>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <TextField
            label=""
            placeholder={fieldConfig.placeholder || "Escribe una extensión"}
            className="w-full pr-10"
            value={newFileType}
            onChange={(e) => setNewFileType(e.target.value)}
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            <OptionTooltip title="Extensiones permitidas: pdf, doc, docx, jpg, jpeg, png, gif, webp, svg, txt, csv, xls, xlsx, ppt, pptx, json, zip, rar, mpp, mpt, dwg, dwf, dxf">
              <div className="text-body-quiet cursor-help">
                <Icon name="circle-info" type="far" className="text-sm" />
              </div>
            </OptionTooltip>
          </div>
        </div>
        <Button
          className="h-11"
          variant="quiet"
          disabled={
            !newFileType.trim() ||
            !ALLOWED_FILE_TYPES.includes(newFileType.trim().toLowerCase())
          }
          onClick={() => {
            if (newFileType.trim()) {
              updateFormValue(fieldConfig.key, [
                ...currentFileTypes,
                newFileType.trim(),
              ]);
              setNewFileType("");
            }
          }}
        >
          <Icon name="plus" type="far" />
        </Button>
      </div>
      {currentFileTypes.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {currentFileTypes.map((fileType: string, index: number) => (
            <Badge
              key={fileType}
              variant="action"
              size="sm"
              className="flex items-center gap-1.5 px-2 py-1 text-action"
            >
              <span>{fileType}</span>
              <Button
                variant="ghost"
                size="icon"
                type="button"
                aria-label="Eliminar"
                className="h-4 w-4 p-0 text-action"
                onClick={() =>
                  updateFormValue(
                    fieldConfig.key,
                    removeAtIndex(currentFileTypes, index),
                  )
                }
              >
                <Icon name="xmark" type="far" className="text-xs" />
              </Button>
            </Badge>
          ))}
        </div>
      )}
      {fieldConfig.helperText && (
        <p className="text-xs text-body-quiet mt-1">{fieldConfig.helperText}</p>
      )}
    </div>
  );
}

export function renderDataCenterField({
  fieldConfig,
  formValues,
  setDataCenterValue,
}: RenderFieldContext) {
  const disableSearch = fieldConfig.customProps?.disableSearch || false;
  return (
    <DataCenterField
      key={fieldConfig.key}
      fieldKey={fieldConfig.key}
      label={fieldConfig.label}
      placeholder={fieldConfig.placeholder}
      helperText={fieldConfig.helperText}
      required={fieldConfig.required}
      selectedTable={formValues[`${fieldConfig.key}_table`] as string}
      selectedColumn={formValues[`${fieldConfig.key}_column`] as string}
      onChange={setDataCenterValue}
      disableSearch={disableSearch}
    />
  );
}
